import { Box, Typography, useTheme } from '@mui/material';
import MuiPopper from '@mui/material/Popper';
import { escape, noop } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import ContentEditable from 'react-contenteditable';

import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { EditableHeader } from '@/components/form/EditableHeader/EditableHeader';
import { PRESENTATION_VARIABLE_CLASS } from '@/components/TextEditor/plugins/PresentationVariablePlugin/PresentationVariableNode';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { usePresentationStringFormatter } from '@/modules/presentation/clientPresentation/presentationString/usePresentationStringFormatter';
import { isKnownVariable } from '@/modules/presentation/clientPresentation/presentationString/usePresentationStringFormatter.formatters';
import { TenantSlideStyle } from '@/modules/slideEditor/coverSlide/CoverSlide.types';
import { bodyTextAlignmentToCss } from '@/modules/slideEditor/shared/bodyTextAlignmentConfig';
import {
  CoverSlideConfigTitleTextSize,
  TITLE_TEXT_SIZE_CONFIG,
} from '@/modules/slideEditor/shared/titleTextSizeConfig';
import { ROMIE_MEDIUM_WEIGHT } from '@/styles/fonts/romie';
import { COLORS } from '@/styles/tokens/colors';
import { TITLE_SERIF_SIZING } from '@/styles/tokens/fonts';
import { zIndexes } from '@/styles/zIndexes';

import { useResolverVersion } from './hooks/useResolverVersion';
import { useTitleVariableTypeahead } from './hooks/useTitleVariableTypeahead';
import {
  SLIDE_EDITOR_HOVER,
  VARIABLE_HIGHLIGHT_HOVER_SX,
  VARIABLE_HIGHLIGHT_SX,
} from './SlideEditor.constants';
import {
  getSelectionOffsetInElement,
  htmlToPlainTextWithLineBreaks,
  normalizeTitleText,
  setCursorOffset,
  TITLE_VARIABLE_ATTRIBUTE,
} from './SlideEditor.titleTextEditor.utils';
import { SlideEditorTitleToolbar } from './SlideEditor.titleToolbar';
import { isClickInsideEditorPortal } from './utils/isClickInsideEditorPortal';

const VARIABLE_TOKEN_REGEX = /\{([^{}]+)\}/g;
// Inline styles applied in edit mode so the highlight persists when the mouse
// leaves the Box to reach the toolbar popper (CSS class rules would not apply then).
const VARIABLE_HIGHLIGHT_STYLE = `background-color: ${COLORS.NAVY[50]}; box-shadow: inset 0 0 0 1px ${COLORS.NAVY[100]}; border-radius: 4px; color: ${COLORS.BLUE[600]}`;

/**
 * Wrap text in a styled span for variable highlighting. Always adds the
 * `presentation-variable` CSS class for parent-controlled backgrounds.
 * When `useInlineBackground` is true (edit mode), also applies inline
 * background-color so the highlight persists even when the mouse leaves
 * the Box (e.g. to reach the toolbar popper).
 */
function wrapVariableSpan(text: string, useInlineBackground: boolean): string {
  const style = useInlineBackground
    ? VARIABLE_HIGHLIGHT_STYLE
    : 'border-radius: 4px';
  const editAttributes = useInlineBackground
    ? ` ${TITLE_VARIABLE_ATTRIBUTE} contenteditable="false"`
    : '';
  return `<span class="${PRESENTATION_VARIABLE_CLASS}" style="${style}"${editAttributes}>${text}</span>`;
}

/** Wrap all `{Variable}` tokens in styled spans without resolving them.
 *  Used in edit mode to visually distinguish variables from plain text. */
function highlightAllVariables(input: string): string {
  return renderVariables(input, (match) => {
    // Append a zero-width space after the span so the cursor lands outside
    // the styled element when typing. Stripped on save by handleEditorBlurChange.
    // useInlineBackground=true so the highlight persists when the mouse
    // leaves the Box to reach the toolbar popper.
    return wrapVariableSpan(escape(match), true) + '\u200B';
  });
}

/** Wrap all `{Variable}` tokens in styled spans, resolving known variables
 *  to their display value. Used in display mode to highlight both resolved
 *  and unresolved variables. */
function highlightFormattedVariables(
  input: string,
  resolveVariable: (variableName: string) => string | null
): string {
  return renderVariables(input, (match, variableName) => {
    // useInlineBackground=false — the parent Box controls the background
    // via CSS class rules (always-on for templates, hover-only otherwise).
    return wrapVariableSpan(
      escape(resolveVariable(variableName) ?? match),
      false
    );
  });
}

/** Escape literal title text and add markup only for recognized variables. */
function renderVariables(
  input: string,
  renderVariable: (match: string, variableName: string) => string
): string {
  let output = '';
  let previousMatchEnd = 0;

  for (const match of input.matchAll(VARIABLE_TOKEN_REGEX)) {
    const matchStart = match.index;
    const matchedText = match[0];
    const variableName = match[1];
    if (matchStart === undefined || !matchedText || !variableName) continue;

    output += escape(input.slice(previousMatchEnd, matchStart));
    output += isKnownVariable(variableName)
      ? renderVariable(matchedText, variableName)
      : escape(matchedText);
    previousMatchEnd = matchStart + matchedText.length;
  }

  return output + escape(input.slice(previousMatchEnd));
}

function renderLineBreaks(html: string, allowLineBreaks: boolean): string {
  return allowLineBreaks ? html.replace(/\n/g, '<br />') : html;
}

/**
 * Where this title editor is rendered. Switches the Romie font weight
 * applied on top of the `h1_display` variant:
 * - `'cover'` (default) — Bold (700). The hero treatment used by cover
 *   slides, the divider slide, and the slide editor's custom-slide
 *   preview, all matching Figma's Title/Serif Bold ramp.
 * - `'content'` — Medium (500). Used by content / refresh-bundle slide
 *   titles (Documents reviewed, Family overview, etc.) which render
 *   the same size as a Bold cover title but in the lighter Medium face
 *   so they don't compete visually with cover-slide hero titles.
 */
export type SlideEditorTitleTextVariant = 'content' | 'cover';

export interface SlideEditorTitleTextProps {
  title: string | null | undefined;
  onChange?: (newTitle: string) => void;
  /**
   * Fires on every keystroke with the sanitized live value (unlike `onChange`,
   * which only fires on blur). Used by the template editor to track in-progress
   * edits and arm the unsaved-changes blocker before the user navigates away.
   * The committed value still flows through `onChange` on blur.
   */
  onInput?: (value: string) => void;
  onClickInput?: () => void;
  placeholder?: string;
  showSuggestions?: boolean;
  /** When true, `{Variable name}` tokens are highlighted with a colored
   *  background. Intended for template editing; live presentations should
   *  leave this off. */
  highlightVariables?: boolean;
  /** When true, suppresses the floating suggestions / variable toolbar
   *  popper and disables the `{` typeahead. Used by the refresh bundle
   *  slides (Documents reviewed, Trusts overview, Family overview, Wills)
   *  whose titles are plain bundle names with no variable support. */
  hideToolbar?: boolean;
  /** Optional tenant-level style overrides for the title text. */
  tenantStyle?: TenantSlideStyle;
  /** See {@link SlideEditorTitleTextVariant}. Defaults to `'cover'`
   *  (Romie Bold). */
  variant?: SlideEditorTitleTextVariant;
  /** When true, Enter inserts line breaks instead of committing the title. */
  allowLineBreaks?: boolean;
}

/**
 * This component is used to display the title across the various client presentation cover slides
 * where the title is editable.
 *
 * **Important:** Any MUI Dialog/Modal that renders this component must set
 * `disableEnforceFocus` on the modal so MUI's focus trap polling doesn't
 * interfere with the contenteditable element.
 */
export function SlideEditorTitleTextEditor({
  title,
  onChange,
  onInput,
  onClickInput,
  placeholder = 'No title',
  showSuggestions,
  highlightVariables,
  hideToolbar = false,
  tenantStyle,
  variant = 'cover',
  allowLineBreaks = false,
}: SlideEditorTitleTextProps) {
  const theme = useTheme();
  const { disabled: formFieldsDisabled } = useFormFieldsDisabled();
  const titleColor = tenantStyle?.textColor ?? theme.palette.primary.main;
  // Tenant-branded title alignment. Left is the default (undefined) and
  // keeps the title's natural fit-content, left-anchored layout. Center/
  // right stretch the title box to full width so `text-align` can shift
  // the heading — matching the body alignment applied to the RTE.
  const titleAlignment = bodyTextAlignmentToCss(tenantStyle?.bodyTextAlignment);
  // T1-5972: titleSizeRem takes precedence over the legacy titleTextSize
  // enum. When set we use the rem value directly and derive a line-height
  // from the legacy 4/3 ratio, so a saved 27pt (= 2.25rem) renders at
  // exactly the proportions the legacy enum spec produced.
  //
  // When neither is set, fall back to Medium — Title/Serif/xl (36/48)
  // per the Foundation ramp (titleTextSizeConfig.ts). Preserves the
  // legacy "unset == Medium" default and keeps each enum bucket
  // (Small/Medium/Large) aligned with a discrete ramp level (l/xl/xxl)
  // so the picker stays meaningful.
  const titleSize =
    tenantStyle?.titleSizeRem != null
      ? {
          fontSize: `${tenantStyle.titleSizeRem}rem`,
          lineHeight: `${(tenantStyle.titleSizeRem * 4) / 3}rem`,
        }
      : TITLE_TEXT_SIZE_CONFIG[
          tenantStyle?.titleTextSize ?? CoverSlideConfigTitleTextSize.Medium
        ];
  // Content-page titles render at a fixed Title/Serif/l (32/44) in Romie
  // Medium (500) — a step down in both size and weight from the
  // cover-slide hero. They don't use the tenant size config (that's
  // cover-only), so `titleSize` above only applies to the cover/divider
  // path, which keeps the h1_display variant's Bold (700) weight.
  const titleSizing =
    variant === 'content'
      ? { ...TITLE_SERIF_SIZING.l, fontWeight: ROMIE_MEDIUM_WEIGHT }
      : titleSize;
  const { resolveVariable } = usePresentationStringFormatter();
  const resolverVersion = useResolverVersion(resolveVariable);
  const [resetKey, setResetKey] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  // The title commits on blur, so block browser navigation while the editor is
  // open — a back/forward or tab close mid-edit would otherwise drop the
  // in-progress title. No-op outside an UnloadPromptProvider.
  useOnBeforeUnloadPrompt(isEditing);
  const savedOffsetRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Keep refs to the current title and editing state so handleMouseDown can
  // read them without the effect needing to re-attach listeners on every change.
  const titleRef = useRef(title);
  titleRef.current = title;
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;

  // Track cursor position within the editable header so we can insert
  // variables at the right place after the dropdown interaction blurs
  // the content-editable.
  useEffect(() => {
    if (!onChange) return;

    const handleSelectionChange = () => {
      const offset = getSelectionOffsetInElement('EditableHeader-input');
      if (offset !== null) {
        savedOffsetRef.current = offset;
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [onChange]);

  // Enter edit mode on focusin, but suppress focusin events that fire
  // immediately after dismissing edit mode. When isEditing flips to false
  // the EditableHeader key changes (edit→display), causing a remount
  // whose focusin can re-enter edit mode, blocking slide transitions.
  // The parent FullScreenModal sets `disableEnforceFocus` so MUI's focus
  // trap won't redirect focus back here after a dismiss.
  const suppressFocusRef = useRef(false);
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !onChange) return;

    let rafId: number | null = null;

    const handleFocusIn = () => {
      if (suppressFocusRef.current) {
        // Ignore focusin during the dismiss→remount transition.
        return;
      }
      setIsEditing((prev) => {
        if (!prev) {
          // Reset the saved offset when transitioning from display to
          // edit mode so a stale cursor position from the formatted
          // display string isn't used against the raw template.
          savedOffsetRef.current = null;
        }
        return true;
      });
    };

    // Dismiss edit mode on clicks outside the container and editable element.
    const handleMouseDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      // Don't dismiss when clicking inside a MUI Menu/Popover portal
      // or the slide editor toolbar, since dropdown menus render outside the container.
      if (isClickInsideEditorPortal(e.target)) {
        return;
      }
      // Stay in edit mode only when clicking on the editable element itself
      // (e.g. placing cursor or selecting text). Clicks on dead space inside
      // the container should dismiss, just like clicks outside it.
      const editable = container.querySelector(
        '[data-testid="EditableHeader-input"]'
      );
      if (editable?.contains(e.target)) {
        return;
      }

      // Read pending edits directly from the DOM before dismissing.
      // Normally EditableHeader's onBlur (triggered by the blur() call
      // below) handles this, but the blur can miss when the contenteditable
      // is not the active element — e.g. after MUI's Menu restores focus to
      // its trigger button on close. Reading the DOM here guarantees we
      // never discard user-typed text.
      // Only run in edit mode — in display mode the contenteditable shows
      // resolved variables (e.g. "John Doe" instead of "{Client Name}") and
      // comparing against the raw template would incorrectly overwrite it.
      if (isEditingRef.current && editable instanceof HTMLElement) {
        const plain = htmlToPlainTextWithLineBreaks(
          editable.innerHTML,
          allowLineBreaks
        );
        const cleaned = normalizeTitleText(plain, allowLineBreaks);
        const currentTitle = titleRef.current || '';
        if (cleaned !== currentTitle) {
          onChange?.(cleaned);
        }
      }

      // Suppress focusin during the dismiss→remount cycle to prevent the
      // focus ping-pong. requestAnimationFrame clears the flag after the
      // render and any resulting focus events have settled.
      suppressFocusRef.current = true;
      rafId = requestAnimationFrame(() => {
        suppressFocusRef.current = false;
        rafId = null;
      });
      // Blur the active element so EditableHeader's onBlur fires and saves
      // any pending edits before the value switches to formatted.
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      setIsEditing(false);
    };

    container.addEventListener('focusin', handleFocusIn);
    // Capture phase so a `stopPropagation()` further down the bubble chain
    // (MUI TreeItem internals on sidebar clicks, third-party drag handles,
    // etc.) can't silently swallow the save. Bubble-phase fires only after
    // every intermediate handler chose not to stop the event — too fragile
    // for a "must-fire" save path. Capture is read-only here (no
    // preventDefault/stopPropagation) so downstream handlers still run.
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => {
      container.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('mousedown', handleMouseDown, true);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        suppressFocusRef.current = false;
      }
    };
  }, [allowLineBreaks, onChange]);

  // Re-focus the editable header after it remounts due to a resetKey change
  // (e.g., after inserting a variable or suggestion). Without resetKey in the
  // dependency array, isEditing stays true and this effect wouldn't re-run.
  // Also restores the cursor to savedOffsetRef so it lands after the inserted
  // content rather than jumping to the beginning.
  useEffect(() => {
    if (isEditing) {
      const el = document.querySelector(
        '[data-testid="EditableHeader-input"]'
      ) as HTMLElement | null;
      if (el) {
        el.focus();
        if (savedOffsetRef.current !== null) {
          setCursorOffset(el, savedOffsetRef.current);
        }
      }
    }
  }, [isEditing, resetKey]);

  const handleSelectSuggestion = useCallback(
    (suggestion: string) => {
      const cleaned = normalizeTitleText(suggestion, allowLineBreaks);
      onChange?.(cleaned);
      setResetKey((k) => k + 1);
      // Place cursor at end of the new suggestion text.
      savedOffsetRef.current = cleaned.length;
    },
    [allowLineBreaks, onChange]
  );

  const handleSelectVariable = useCallback(
    (variableText: string) => {
      const token = `{${variableText}}`;
      const currentValue = title || '';
      const offset = savedOffsetRef.current;
      const insertAt =
        offset !== null && offset >= 0 && offset <= currentValue.length
          ? offset
          : currentValue.length;
      const charBefore = currentValue[insertAt - 1];
      // ContentEditable may store non-breaking spaces (\u00a0) instead of
      // regular spaces, so check for both.
      const needsSpace =
        insertAt > 0 && charBefore !== ' ' && charBefore !== '\u00a0';
      const newValue = normalizeTitleText(
        currentValue.slice(0, insertAt) +
          (needsSpace ? ' ' : '') +
          token +
          currentValue.slice(insertAt),
        allowLineBreaks
      );
      onChange?.(newValue);
      setResetKey((k) => k + 1);
      // Place cursor right after the inserted token so the user can
      // continue typing without clicking the title again.
      savedOffsetRef.current = Math.min(
        insertAt + (needsSpace ? 1 : 0) + token.length,
        newValue.length
      );
    },
    [allowLineBreaks, onChange, title]
  );

  const { menuElement: typeaheadMenu } = useTitleVariableTypeahead({
    // Refresh bundle slides opt out of the `{Variable}` typeahead by
    // passing `hideToolbar` — the title is a plain bundle name with no
    // variable support, so suppress the typeahead listeners entirely.
    isEditing: isEditing && !!onChange && !hideToolbar,
    resetKey,
    savedOffsetRef,
    onChange: onChange ?? noop,
    setResetKey,
  });

  // When preserveHtml is active, EditableHeader passes raw HTML on blur.
  // Strip it back to plain text before forwarding to the parent.
  // Also remove zero-width spaces inserted by highlightAllVariables and
  // normalize whitespace-only content (e.g. non-breaking spaces left behind
  // by contenteditable) to empty string so the placeholder reappears.
  const handleEditorBlurChange = useCallback(
    (htmlValue: string) => {
      const plain = htmlToPlainTextWithLineBreaks(htmlValue, allowLineBreaks);
      const cleaned = normalizeTitleText(plain, allowLineBreaks);
      onChange?.(cleaned);
    },
    [allowLineBreaks, onChange]
  );

  // Per-keystroke notification with the same sanitized plain text as the blur
  // path, so the template editor can track live edits without waiting for blur.
  const handleEditorInput = useCallback(
    (htmlValue: string) => {
      const plain = htmlToPlainTextWithLineBreaks(htmlValue, allowLineBreaks);
      const cleaned = normalizeTitleText(plain, allowLineBreaks);
      onInput?.(cleaned);
    },
    [allowLineBreaks, onInput]
  );

  if (onChange) {
    const showToolbar = isEditing && !hideToolbar;
    const safeTitle = title || '';

    let editableValue: string;
    if (isEditing) {
      // Always highlight raw {Variable} tokens during editing so they're
      // visually distinct. Safe because handleEditorBlurChange strips HTML.
      editableValue = renderLineBreaks(
        highlightAllVariables(safeTitle),
        allowLineBreaks
      );
    } else {
      // Always wrap resolved variables in spans with the CSS class so the
      // parent Box can control the highlight via CSS (always-on for
      // templates, hover-only for live presentations).
      editableValue = renderLineBreaks(
        highlightFormattedVariables(safeTitle, resolveVariable),
        allowLineBreaks
      );
    }

    return (
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          // The inner hover box is `width: fit-content` (it shrink-wraps
          // the title), so `text-align` can't move it. Center/right
          // alignment instead stretches this container to full width and
          // uses flex `justify-content` to shift the fit-content title
          // box. Left keeps the natural fit-content, left-anchored layout.
          width: titleAlignment ? '100%' : 'fit-content',
          ...(titleAlignment && {
            display: 'flex',
            justifyContent: titleAlignment === 'right' ? 'flex-end' : 'center',
          }),
        }}
      >
        <MuiPopper
          open={showToolbar}
          anchorEl={containerRef.current}
          placement="top-start"
          data-slide-editor-toolbar
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
          sx={{ zIndex: zIndexes.POPOVER }}
        >
          <SlideEditorTitleToolbar
            showSuggestions={showSuggestions}
            onSelectSuggestion={handleSelectSuggestion}
            onSelectVariable={handleSelectVariable}
          />
        </MuiPopper>
        {typeaheadMenu}
        <Box
          onMouseDown={(event) => {
            if (isEditing || formFieldsDisabled) return;

            // Display mode contains resolved variable values. Do not let the
            // browser focus or mutate that DOM. Mount the raw-token editor
            // first, then the focus effect above places the caret at the end.
            event.preventDefault();
            savedOffsetRef.current = safeTitle.length;
            setIsEditing(true);
          }}
          sx={[
            SLIDE_EDITOR_HOVER,
            highlightVariables
              ? VARIABLE_HIGHLIGHT_SX
              : VARIABLE_HIGHLIGHT_HOVER_SX,
          ]}
        >
          <EditableHeader
            key={
              isEditing
                ? `edit-${resetKey}`
                : `display-${resetKey}-v${resolverVersion}`
            }
            value={editableValue}
            placeholder={placeholder}
            onChange={isEditing ? handleEditorBlurChange : noop}
            onInput={isEditing && onInput ? handleEditorInput : undefined}
            typographyVariant="h1_display"
            // The flex wrapper above centers the fit-content box, which
            // only shifts a single-line title. A wrapping title grows the
            // box to full column width, making the flex a no-op — so also
            // set text-align here to center/right the wrapped lines,
            // matching the read-only path below.
            typographyProps={{
              sx: [
                titleSizing,
                ...(titleAlignment ? [{ textAlign: titleAlignment }] : []),
              ],
            }}
            color={titleColor}
            onClick={onClickInput}
            showPencilIcon={false}
            showEditBorder={false}
            preserveHtml={isEditing}
            allowLineBreaks={allowLineBreaks}
            disabled={!isEditing}
          />
        </Box>
      </Box>
    );
  }
  if (!title) return null;

  // Always wrap resolved variables in spans so CSS can control the
  // highlight (always-on for templates, no highlight for read-only
  // non-template — there's no hover interaction in this path).
  const formattedTitle = renderLineBreaks(
    highlightFormattedVariables(title, resolveVariable),
    allowLineBreaks
  );

  // because react-contenteditable in EditableHeader returns an HTML-ified string,
  // also use that component to display them on output so we don't need to decode or
  // manually escape them.
  //
  // `titleSizing` is applied to BOTH the outer Typography (to collapse
  // its variant strut from the provider's xxl 48/62 default down to the
  // rendered size) and the inner ContentEditable span (which sets its
  // own line-box). Without the outer sx, the Typography strut would
  // exceed the span's line-height and add ~14px of dead vertical space
  // on every read-only title (refresh-bundle continuation pages, print,
  // and PDF).
  return (
    <Typography
      variant="h1_display"
      color={titleColor}
      sx={[
        titleSizing,
        ...(titleAlignment ? [{ textAlign: titleAlignment }] : []),
        ...(highlightVariables ? [VARIABLE_HIGHLIGHT_SX] : []),
      ]}
    >
      <ContentEditable
        html={formattedTitle}
        tagName="span"
        // This branch only renders when `onChange` is absent. Disable the
        // underlying contenteditable element so output-only titles cannot
        // appear editable or accept local DOM changes.
        disabled
        style={{
          padding: '4px',
          ...(allowLineBreaks && {
            whiteSpace: 'pre-wrap',
            display: 'inline-block',
            padding: '0 4px',
          }),
          ...titleSizing,
        }}
        onChange={noop}
      />
    </Typography>
  );
}
